// Actualización de ApiClient.js
// src/services/apiClient.js
import AuthService from './authService';
const API_URL = 'https://chat-whatsapp-backend.sugaronweb.com.mx/api';

class ApiClient {
    static refreshTokenPromise = null;

    static async fetch(url, options = {}) {
        options.headers = options.headers || {};
        const token = AuthService.getToken();
        if (token) {
            options.headers['Authorization'] = `Bearer ${token}`;
        }

        try {
            const response = await fetch(url, options);

            if (response.status === 401) {
                // Si ya hay un refresh en progreso, esperar a que termine
                if (!this.refreshTokenPromise) {
                    this.refreshTokenPromise = AuthService.refreshToken()
                        .finally(() => {
                            this.refreshTokenPromise = null;
                        });
                }
                await this.refreshTokenPromise;
                // Reintentar con nuevo token
                const newToken = AuthService.getToken();
                options.headers['Authorization'] = `Bearer ${newToken}`;
                return await fetch(url, options);
            }

            // Si esperamos un blob, devolver directamente la respuesta como blob
            if (options.responseType === 'blob') {
                if (!response.ok) {
                    throw new Error('Error en la petición');
                }
                return await response.blob();
            }

            // Para respuestas JSON (comportamiento predeterminado)
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Error en la petición');
            }
            return data;
        } catch (error) {
            if (error.message === 'Sesión expirada') {
                AuthService.logout();
            }
            throw error;
        }
    }

    static getContacts(userId) {
        return this.fetch(`${API_URL}/contacts/${userId}`);
    }

    // NUEVO MÉTODO: Obtener mensajes iniciales en lote para todos los contactos
    static getBatchInitialMessages(limit = 20) {
        const queryParams = new URLSearchParams({
            limit: limit.toString()
        }).toString();
        return this.fetch(`${API_URL}/messages/batch?${queryParams}`);
    }

    // Método actualizado para soportar paginación
    static getMessages(contactId, options = {}) {
        // Si options es indefinido, inicializarlo como un objeto vacío
        options = options || {};
        const { page = 1, limit = 20 } = options;
        const queryParams = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString()
        }).toString();
        return this.fetch(`${API_URL}/messages/${contactId}?${queryParams}`);
    }

    static async sendMessage(data) {
        const options = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data)
        };
        return this.fetch(`${API_URL}/messages/send`, options);
    }

    static async sendMessageToContact(contactId, messageContent) {
        const data = {
            contact_id: contactId,
            content: messageContent
        };
        return this.sendMessage(data);
    }

    static async updateMessage(contactId) {
        const options = {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
            }
        };
        return this.fetch(`${API_URL}/messages/updateMessageStatus/${contactId}`, options);
    }
    // Modificar para no incluir los parámetros de consulta
    static getSecureDownloadUrl(filePath, customFilename = null) {
        // Extraer la parte de la ruta después de "storage/"
        const pathMatch = filePath.match(/\/storage\/(.+)$/);
        if (!pathMatch || !pathMatch[1]) {
            throw new Error('Formato de ruta inválido');
        }

        // Codificar la ruta en base64
        const encodedPath = btoa(pathMatch[1]);

        // Construir los parámetros
        const params = {
            path: encodedPath
        };

        if (customFilename) {
            params.filename = customFilename;
        }

        return params;
    }

    // Nuevo método para iniciar descargas
    static initiateSecureDownload(filePath, customFilename = null, onSuccess = null, onError = null) {
        try {
            const params = this.getSecureDownloadUrl(filePath, customFilename);
            const queryParams = new URLSearchParams(params).toString();

            this.fetch(`${API_URL}/secure-download?${queryParams}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/octet-stream',
                },
                responseType: 'blob'
            })
                .then(response => {
                    const url = window.URL.createObjectURL(new Blob([response]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', customFilename || 'imagen.jpg');
                    document.body.appendChild(link);
                    link.click();

                    setTimeout(() => {
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                        if (onSuccess) onSuccess();
                    }, 100);
                })
                .catch(error => {
                    // console.error('Error al descargar:', error);
                    if (onError) onError(error);
                });
        } catch (error) {
            // console.error("Error al preparar la descarga:", error);
            if (onError) onError(error);
            throw error;
        }
    }
    static async sendFileToContact(contactId, formData, fileType, caption = null) {
        // Añadir información adicional al FormData
        formData.append('contact_id', contactId);
        formData.append('type', fileType);
        if (caption) {
            formData.append('caption', caption);
        }
        
        const options = {
            method: 'POST',
            headers: {
                // No incluyas 'Content-Type' - el navegador lo establece automáticamente con boundary
            },
            body: formData
        };
        
        return this.fetch(`${API_URL}/messages/send-file`, options);
    }
    static async sendCurrentMode(contactId, currentMode) {
        const data = {
            contact_id: contactId,
            current_mode: currentMode
        };
        const options = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data)
        };
        return this.fetch(`${API_URL}/contacts/current-mode`, options);
    }
}

export default ApiClient;